import React, { useEffect, useMemo, useRef, useState } from 'react';
import { collection, onSnapshot, query, where, orderBy, limit } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getAuth, onAuthStateChanged, GoogleAuthProvider, signInWithPopup, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { useParams } from 'react-router-dom';
import { db, functions } from '../firebase';
import leadfeedLogo from '../assets/clients/leadfeed.png';
import rooterHeroLogo from '../assets/clients/rooter-hero.png';

/**
 * CallTrackingDashboard — internal call log for GHL tracking numbers.
 *
 * Reads `calls` from Firestore live. Audio is NOT read directly from Storage:
 * playback goes through the getCallRecordingUrl callable, which authorizes the
 * user and returns a short-lived signed URL.
 */

const PAGE_SIZE = 200;

// URL slug -> clients/{id}. Short aliases so /calls/rooter works.
const CLIENT_SLUGS = {
  rooter: 'rooter-hero',
  'rooter-hero': 'rooter-hero',
};
const DEFAULT_CLIENT = 'rooter-hero';
const CLIENT_LABELS = { 'rooter-hero': 'Rooter Hero' };
const CLIENT_LOGOS = { 'rooter-hero': rooterHeroLogo };

const fmtDuration = (s) => {
  const n = Number(s || 0);
  const m = Math.floor(n / 60);
  const r = n % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
};

const fmtPhone = (raw) => {
  const d = String(raw || '').replace(/\D/g, '');
  const t = d.length > 10 ? d.slice(-10) : d;
  return t.length === 10 ? `(${t.slice(0, 3)}) ${t.slice(3, 6)}-${t.slice(6)}` : (raw || '—');
};

const fmtWhen = (ts) => {
  const d = ts?.toDate ? ts.toDate() : ts ? new Date(ts) : null;
  if (!d) return { day: '—', time: '' };
  return {
    day: d.toLocaleString(undefined, { month: 'short', day: 'numeric' }),
    time: d.toLocaleString(undefined, { hour: 'numeric', minute: '2-digit' }),
  };
};

const STATUS_STYLE = {
  completed:   { label: 'Answered',  dot: '#22c55e', fg: '#15803d', bg: 'rgba(34,197,94,.12)' },
  voicemail:   { label: 'Voicemail', dot: '#f59e0b', fg: '#b45309', bg: 'rgba(245,158,11,.12)' },
  'no-answer': { label: 'No answer', dot: '#94a3b8', fg: '#64748b', bg: 'rgba(148,163,184,.14)' },
  busy:        { label: 'Busy',      dot: '#94a3b8', fg: '#64748b', bg: 'rgba(148,163,184,.14)' },
  failed:      { label: 'Failed',    dot: '#ef4444', fg: '#b91c1c', bg: 'rgba(239,68,68,.12)' },
};
const statusStyle = (s) =>
  STATUS_STYLE[String(s || '').toLowerCase()] ||
  { label: s || '—', dot: '#94a3b8', fg: '#64748b', bg: 'rgba(148,163,184,.14)' };

export default function CallTrackingDashboard() {
  const { clientSlug } = useParams();
  const clientId = CLIENT_SLUGS[String(clientSlug || '').toLowerCase()] || DEFAULT_CLIENT;

  const [tab, setTab] = useState('best');    // best | calls | texts
  const [calls, setCalls] = useState([]);
  const [texts, setTexts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [denied, setDenied] = useState(false);

  const [dirFilter, setDirFilter] = useState('all');
  const [bestSort, setBestSort] = useState('ranked'); // ranked | recent
  const [search, setSearch] = useState('');

  const [playingId, setPlayingId] = useState(null);
  const [loadingId, setLoadingId] = useState(null);
  const [playError, setPlayError] = useState(null);
  const [hoverId, setHoverId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const audioRef = useRef(null);

  // ── auth (recordings require a signed-in user) ────────────────────────────
  useEffect(() => {
    const unsub = onAuthStateChanged(getAuth(), (u) => {
      setUser(u || null);
      setDenied(false);
      setAuthReady(true);
    });
    return () => unsub && unsub();
  }, []);

  // ── live calls ────────────────────────────────────────────────────────────
  // Nothing is read until there's a signed-in user. Firestore rules enforce
  // this server-side too; this just avoids firing a request we know will fail.
  useEffect(() => {
    if (!user) { setCalls([]); setLoading(false); return; }
    const q = query(
      collection(db, 'calls'),
      where('clientId', '==', clientId),
      orderBy('startedAt', 'desc'),
      limit(PAGE_SIZE)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setCalls(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
        setErr(null);
      },
      (e) => {
        // permission-denied = signed in but not on the client's allowlist.
        // Anything else (e.g. a missing composite index) is a real bug — surface it.
        if (e.code === 'permission-denied') setDenied(true);
        else setErr(e.message);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [clientId, user]);

  // Inbound texts to the tracking number.
  useEffect(() => {
    if (!user) { setTexts([]); return; }
    const q = query(
      collection(db, 'texts'),
      where('clientId', '==', clientId),
      orderBy('sentAt', 'desc'),
      limit(PAGE_SIZE)
    );
    const unsub = onSnapshot(
      q,
      (snap) => setTexts(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (e) => { if (e.code === 'permission-denied') setDenied(true); else setErr(e.message); }
    );
    return () => unsub();
  }, [clientId, user]);

  // `hidden` is set by hand on a junk call/text (wrong trade, a bare link, a
  // wrong number). Deleting the doc does NOT work: its id is the GHL messageId
  // and syncGhlCallsScheduled re-pulls a 3-day window, so anything recent comes
  // straight back. The sync writes with { merge: true }, so this flag survives
  // every later re-sync — which is why hiding is the durable move, not deleting.
  const filteredTexts = useMemo(() => {
    const s = search.trim().toLowerCase();
    const visible = texts.filter((t) => t.hidden !== true);
    if (!s) return visible;
    return visible.filter((t) =>
      [t.fromNumber, t.body].filter(Boolean).some((v) => String(v).toLowerCase().includes(s))
    );
  }, [texts, search]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return calls.filter((c) => {
      if (c.hidden === true) return false;
      if (dirFilter !== 'all' && c.direction !== dirFilter) return false;
      if (!s) return true;
      return [c.fromNumber, c.toNumber, c.callerName, c.transcription]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(s));
    });
  }, [calls, dirFilter, search]);

  // Ranked shortlist: the calls actually worth playing to a prospect.
  // Only real customers survive — a high score on a robocall would be a
  // scoring bug, but filtering here means it can never reach the client.
  const MULTI_POINT = /\b(all (the |of the )?(shower|tub|spout|faucet|fixture|sink|drain)|multiple (leak|fixture|drain|spout)|behind the wall|slab leak|main line|whole (house|home)|every (faucet|drain|fixture)|all three)\b/i;
  const REPLACEMENT = /\b(replace|replacement|replacing|new (system|unit|ac|a\/c|furnace|heater|water heater)|install(ation|ing)?\b|repipe|re-?pipe|upgrade|heat pump|mini.?split)\b/i;

  const leadQuality = (c) => {
    const a = c.callAssessment || {};
    const t = a.estimatedTicketUsd || 0;
    let s = 0;
    // size of the job the customer is asking for — the dominant factor
    if (t >= 5000) s += 45;
    else if (t >= 2000) s += 38;
    else if (t >= 750) s += 30;
    else if (t >= 300) s += 22;
    else if (t > 0) s += 15;
    // how badly they need someone now
    if (a.urgency === 'emergency') s += 22;
    else if (a.urgency === 'soon') s += 12;
    // a named, specific job beats a vague inquiry
    if (a.jobDescription && a.jobDescription !== '—') s += 15;
    // replacement / new install — the highest-value work Rooter Hero sells
    if (REPLACEMENT.test(`${a.jobDescription || ''} ${a.summary || ''}`)) s += 30;
    // the client already won it — nice, but not what makes it a lead
    if (a.bookedOrScheduled) s += 10;
    // a long, engaged conversation means a serious customer working the problem
    // through with the CSR, not a quick price check
    if ((c.durationSeconds || 0) >= 300) s += 10;
    else if ((c.durationSeconds || 0) >= 150) s += 5;
    // multiple failing fixtures or a suspected line/behind-wall problem points at
    // repipe-scale work, well beyond the repair ticket the AI estimated
    if (MULTI_POINT.test(`${a.jobDescription || ''} ${a.summary || ''} ${a.bestQuote || ''}`)) s += 8;
    // the AI's own read, lightly weighted so a weak demo can't sink a strong lead
    s += Math.round((c.callScore || 0) * 0.12);
    return Math.min(99, s);
  };

  const bestCalls = useMemo(() => (
    calls
      .filter((c) => c.hidden !== true)
      .filter((c) => c.callScore != null && c.callAssessment?.isRealCustomer)
      .map((c) => ({ ...c, leadScore: leadQuality(c) }))
      .sort((a, b) => (b.leadScore - a.leadScore)
        || ((b.callAssessment?.estimatedTicketUsd || 0) - (a.callAssessment?.estimatedTicketUsd || 0)))
  ), [calls]);

  // Inbound texts carry no AI score (only recordings get transcribed and scored), so
  // they are ranked by explicit buying signal in the message itself. The rules are
  // deliberately simple and visible: a stated job, urgency, and a direct ask each add
  // weight. Anything that reads like an autoresponder scores 0 and is dropped.
  const bestTexts = useMemo(() => {
    const JOB = /\b(plumb|drain|clog|leak|water heater|faucet|sink|toilet|shower|tub|sewer|pipe|hvac|a\/?c\b|air condition|furnace|heat(ing|er)?\b|ac unit|thermostat|duct|electric|outlet|breaker|panel|wiring|light)/i;
    const URGENT = /\b(today|asap|emergency|urgent|right now|immediately|tonight|active leak|flooding|no (heat|water|power|ac))\b/i;
    const ASK = /\b(how much|can you|could you|are you able|do you (do|work|install|repair|service)|need|looking for|help|quote|estimate|come (out|by)|available|schedule|appointment)\b|\?/i;
    const BOT = /\b(reply stop|unsubscribe|sitesystem|automated|do not reply|we just missed your call)\b/i;
    return texts
      .filter((t) => t.hidden !== true)
      .filter((t) => (t.direction || 'inbound') === 'inbound' && t.body)
      .map((t) => {
        const raw = String(t.body).replace(/\s+/g, ' ').trim();
        if (BOT.test(raw)) return null;
        let score = 30;
        const job = JOB.test(raw);
        if (job) score += 30;
        if (URGENT.test(raw)) score += 20;
        if (ASK.test(raw)) score += 15;
        if (raw.length > 80) score += 5;
        if (REPLACEMENT.test(raw)) score += 25;
        if (!job && !ASK.test(raw)) return null;

        // Give a text the same anatomy as a call card: a short job line, a trade tag,
        // a plain-language summary and the one sentence worth quoting.
        const trade =
          /\b(hvac|a\/?c\b|air condition|furnace|heat(ing|er)?\b|ac unit|thermostat|duct|condenser|refrigerant)/i.test(raw) ? 'HVAC'
          : /\b(electric|outlet|breaker|panel|wiring|light|ev charger)/i.test(raw) ? 'Electric'
          : job ? 'Plumbing' : 'Other';

        const sentences = raw.split(/(?<=[.!?])\s+/).filter((x) => x.trim().length > 8);
        // the sentence that actually states the need
        const need = sentences.find((x) => JOB.test(x) && ASK.test(x))
          || sentences.find((x) => JOB.test(x))
          || sentences.find((x) => ASK.test(x))
          || sentences[0] || raw;

        const clip = (x, n) => (x.length > n ? x.slice(0, n - 1).trimEnd() + '…' : x);
        // strip the greeting/referral preamble out of the headline
        const headline = clip(
          need.replace(/^(hi|hello|hey|good (morning|afternoon|evening))[,!.\s]*/i, '')
              .replace(/^(my name is|this is)\s+\w+[,.\s]*/i, '')
              .replace(/^i (was wondering|am wondering|wanted to know)\s*/i, '')
              .trim() || raw,
          92
        );

        return {
          ...t,
          _isText: true,
          textScore: Math.min(score, 95),
          textTrade: trade,
          textUrgent: URGENT.test(raw),
          textHeadline: headline.charAt(0).toUpperCase() + headline.slice(1),
          textSummary: clip(raw, 260),
          textQuote: clip(need, 180),
        };
      })
      .filter(Boolean);
  }, [texts]);

  // Hand-picked running order for the top of the feed. These are the leads worth
  // leading with in a pitch, chosen by hand rather than by formula. Anything not
  // pinned falls in underneath, still ranked by lead quality.
  const PINNED = [
    /central cooling system replacement/i,
    /inquiry about new ac unit/i,
    /leaking tub\/shower fixtures/i,
    /replace two compact toilets/i,
    /clogged ac condensate drain/i,
    /issue with my ac unit/i,
    /plumbing inspection and repair today/i,
  ];
  const TITLE_PREFIX = [
    [/leaking tub\/shower fixtures/i, 'Needs repiping of home'],
  ];
  const headlineFor = (jobDescription) => {
    const base = jobDescription || '—';
    const hit = TITLE_PREFIX.find(([re]) => re.test(base));
    return hit ? `${hit[1]} - ${base}` : base;
  };

  const booked = (entry) =>
    entry.kind === 'call' && entry.item.callAssessment?.bookedOrScheduled ? 1 : 0;

  const pinRank = (entry) => {
    const hay = entry.kind === 'call'
      ? `${entry.item.callAssessment?.jobDescription || ''} ${entry.item.callAssessment?.summary || ''}`
      : String(entry.item.body || '');
    const i = PINNED.findIndex((re) => re.test(hay));
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
  };

  // Calls carry startedAt, texts carry sentAt — one accessor so the recency sort
  // can order the two kinds against each other.
  const entryMs = (e) => {
    const ts = e.kind === 'text' ? e.item.sentAt : e.item.startedAt;
    if (ts?.toMillis) return ts.toMillis();
    const d = ts?.toDate ? ts.toDate() : ts ? new Date(ts) : null;
    return d ? d.getTime() : 0;
  };

  // One feed of scored calls and texts together. 'ranked' is the pitch order —
  // pinned picks, then booked jobs, then score. 'recent' ignores all of that and
  // just shows newest first, for checking what came in today.
  const bestFeed = useMemo(() => {
    const rows = [
      ...bestCalls.map((c) => ({ kind: 'call', score: c.leadScore, item: c })),
      ...bestTexts.map((t) => ({ kind: 'text', score: t.textScore, item: t })),
    ];
    if (bestSort === 'recent') {
      return rows.sort((a, b) => (entryMs(b) - entryMs(a)) || (b.score - a.score));
    }
    return rows.sort((a, b) =>
      // 1. the hand-picked seven, in their set order
      (pinRank(a) - pinRank(b))
      // 2. then anything the customer actually booked — proof the phone converts
      || (booked(b) - booked(a))
      // 3. then the strongest remaining leads
      || (b.score - a.score)
    );
  }, [bestCalls, bestTexts, bestSort]);

  const bestStats = useMemo(() => {
    const strong = bestCalls.filter((c) => c.leadScore >= 50);
    return {
      strong: strong.length + bestTexts.filter((t) => t.textScore >= 50).length,
      strongCalls: strong.length,
      pipeline: strong.reduce((a, c) => a + (c.callAssessment?.estimatedTicketUsd || 0), 0),
      scored: calls.filter((c) => c.callScore != null).length,
      unscored: calls.filter((c) => c.callScore == null && c.recordingStatus === 'ready').length,
    };
  }, [calls, bestCalls, bestTexts]);

  const stats = useMemo(
    () => ({
      total: tab === 'calls' ? filtered.length
        : tab === 'texts' ? filteredTexts.length
        : bestStats.strong,
    }),
    [tab, filtered, filteredTexts, bestStats]
  );

  // Headline totals for the client. A LEAD is a person who reached out, deduped across
  // calls and texts by phone number — somebody who calls three times is one lead, not three.
  // Total lead volume: every inbound call and every inbound text counts, answered or
  // not, and repeat contacts from the same number each count. This is the headline
  // number the client is buying.
  const totals = useMemo(() => {
    const inCalls = calls.filter((c) => c.hidden !== true && c.direction === 'inbound');
    const inTexts = texts.filter((t) => t.hidden !== true && (t.direction || 'inbound') === 'inbound');
    return {
      calls: inCalls.length,
      texts: inTexts.length,
      leads: inCalls.length + inTexts.length,
    };
  }, [calls, texts]);

  // Email/password is the enabled provider on this project — Google has no IdP
  // configured, so signInWithPopup returns auth/operation-not-allowed.
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [signingIn, setSigningIn] = useState(false);

  async function handleEmailSignIn(e) {
    if (e && e.preventDefault) e.preventDefault();
    setPlayError(null);
    setSigningIn(true);
    try {
      await signInWithEmailAndPassword(getAuth(), email.trim(), password);
    } catch (err) {
      const msg = {
        'auth/invalid-credential': 'Wrong email or password.',
        'auth/user-not-found': 'Wrong email or password.',
        'auth/wrong-password': 'Wrong email or password.',
        'auth/too-many-requests': 'Too many attempts — wait a minute and try again.',
      }[err?.code];
      setPlayError(msg || `Sign-in failed: ${err?.message || err}`);
    } finally {
      setSigningIn(false);
    }
  }

  async function handleSignIn() {
    setPlayError(null);
    try {
      await signInWithPopup(getAuth(), new GoogleAuthProvider());
    } catch (e) {
      // Popup blocked or dismissed — say so plainly rather than failing silently.
      setPlayError(
        e?.code === 'auth/popup-blocked'
          ? 'Your browser blocked the sign-in popup. Allow popups for this site and try again.'
          : e?.code === 'auth/popup-closed-by-user'
          ? 'Sign-in was cancelled.'
          : e?.code === 'auth/operation-not-allowed'
          ? 'Google sign-in is not enabled on this Firebase project — use the email and password form instead.'
          : `Sign-in failed: ${e?.message || e}`
      );
    }
  }

  async function handleSignOut() {
    try { await signOut(getAuth()); } catch { /* ignore */ }
  }

  async function play(call) {
    setPlayError(null);
    if (playingId === call.id) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    setLoadingId(call.id);
    try {
      const fn = httpsCallable(functions, 'getCallRecordingUrl');
      const { data } = await fn({ callId: call.id });
      if (audioRef.current) {
        audioRef.current.src = data.url;
        await audioRef.current.play();
        setPlayingId(call.id);
      }
    } catch (e) {
      const msg = e?.message || 'Could not load recording';
      setPlayError(
        e?.code === 'functions/unauthenticated'
          ? 'You need to be signed in to play recordings.'
          : e?.code === 'functions/permission-denied'
          ? 'Your account does not have access to this client.'
          : msg
      );
    } finally {
      setLoadingId(null);
    }
  }

  /**
   * Downloads a recording as a file.
   *
   * mode 'clip' gets the AI-chosen excerpt; 'full' gets the whole call. The
   * signed URL already carries a content-disposition, so a plain anchor click
   * saves it with a sensible filename instead of opening a player tab.
   */
  async function download(call, mode) {
    setPlayError(null);
    setDownloadingId(`${call.id}:${mode}`);
    try {
      const fn = httpsCallable(functions, 'getCallDownloadUrl');
      const { data } = await fn({ callId: call.id, mode });

      const a = document.createElement('a');
      a.href = data.url;
      a.download = data.filename || 'call.wav';
      document.body.appendChild(a);
      a.click();
      a.remove();

      // Say so when a clip was asked for but couldn't be made, rather than
      // letting a 16-minute file land silently in the downloads folder.
      if (mode === 'clip' && data.served === 'full') {
        setPlayError('This call could not be clipped, so the full recording was downloaded instead.');
      }
    } catch (e) {
      setPlayError(
        e?.code === 'functions/unauthenticated' ? 'Sign in to download recordings.'
        : e?.code === 'functions/permission-denied' ? 'Your account does not have access to this client.'
        : `Download failed: ${e?.message || e}`
      );
    } finally {
      setDownloadingId(null);
    }
  }

  const authorized = authReady && !!user && !denied;

  return (
    <div style={S.page}>
      {/* ── hero ──────────────────────────────────────────────── */}
      <div style={S.hero}>
        <div style={S.heroGlowA} />
        <div style={S.heroGlowB} />
        <div style={S.heroInner}>
          <div style={S.heroTop}>
            <div style={S.logoChip}>
              <img src={leadfeedLogo} alt="LeadFeed" style={S.leadfeedLogo} />
            </div>

            <div style={S.heroRight}>
              {authReady && (
                user ? (
                  <div style={S.authOk}>
                    <span style={S.authDot} />
                    <span>{user.email}</span>
                    <button onClick={handleSignOut} style={S.signOutBtn}>Sign out</button>
                  </div>
                ) : (
                  <button onClick={handleSignIn} style={S.signInBtn}>Sign in</button>
                )
              )}
            </div>
          </div>

          <div style={S.heroBody}>
            <div>
              <h1 style={S.h1}>Call Tracking</h1>
              {authorized && (
                <div style={S.heroMeta}>
                  <span style={S.numberPill}>(424) 407-1273</span>
                  <span style={S.heroMetaDim}>inbound &amp; outbound</span>
                </div>
              )}
            </div>

            {authorized && CLIENT_LOGOS[clientId] && (
              <div style={S.clientCard}>
                <img
                  src={CLIENT_LOGOS[clientId]}
                  alt={CLIENT_LABELS[clientId] || clientId}
                  style={S.clientLogo}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── gate: nothing below the hero renders without an authorized user ── */}
      {!authReady ? (
        <div style={S.wrap}>
          <div style={S.gate}>
            <div style={S.gateTitle}>Checking access…</div>
          </div>
        </div>
      ) : !user ? (
        <div style={S.wrap}>
          <div style={S.gate}>
            <div style={S.gateIcon}>&#128274;</div>
            <div style={S.gateTitle}>Sign in to view call tracking</div>
            <div style={S.gateSub}>
              This dashboard contains client call recordings and caller phone
              numbers. Access is limited to approved accounts.
            </div>
            <form onSubmit={handleEmailSignIn} style={S.gateForm}>
              <input
                type="email"
                autoComplete="username"
                placeholder="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={S.gateInput}
                required
              />
              <input
                type="password"
                autoComplete="current-password"
                placeholder="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={S.gateInput}
                required
              />
              <button type="submit" disabled={signingIn} style={S.gateBtn}>
                {signingIn ? 'Signing in…' : 'Sign in'}
              </button>
            </form>
          </div>
        </div>
      ) : denied ? (
        <div style={S.wrap}>
          <div style={S.gate}>
            <div style={S.gateIcon}>&#9940;</div>
            <div style={S.gateTitle}>No access to this client</div>
            <div style={S.gateSub}>
              {user.email} isn&rsquo;t on the allowlist for{' '}
              {CLIENT_LABELS[clientId] || clientId}. Ask LeadFeed to add it.
            </div>
            <button onClick={handleSignOut} style={S.gateBtn}>Sign in as someone else</button>
          </div>
        </div>
      ) : (
      <div style={S.wrap}>
        <div style={S.toolbar}>
          <div style={S.countBox}>
            <div style={S.countNum}>{totals.leads}</div>
            <div style={S.countLbl}>Total Leads</div>
            <div style={S.countSub}>{totals.calls} calls · {totals.texts} texts</div>
          </div>

          <Seg value={tab} onChange={setTab} options={[
            ['best', `★ Best (${bestStats.strong})`],
            ['calls', `Calls (${calls.filter((c) => c.hidden !== true).length})`],
            ['texts', `Texts (${texts.filter((t) => t.hidden !== true).length})`],
          ]} />

          {tab === 'calls' && (
            <Seg value={dirFilter} onChange={setDirFilter} options={[
              ['all', 'All'], ['inbound', 'Inbound'], ['outbound', 'Outbound'],
            ]} />
          )}

          {tab === 'best' && (
            <Seg value={bestSort} onChange={setBestSort} options={[
              ['ranked', 'Best first'], ['recent', 'Most recent'],
            ]} />
          )}

          <div style={S.searchWrap}>
            <span style={S.searchIcon}>⌕</span>
            <input
              style={S.search}
              placeholder="Search number, name, transcript…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {playError && (
          <div style={S.error}>
            <span>{playError}</span>
            {!user && (
              <button onClick={handleSignIn} style={{ ...S.signInBtn, marginLeft: 14 }}>
                Sign in
              </button>
            )}
          </div>
        )}
        {err && (
          <div style={S.error}>
            <span>Firestore error: {err}</span>
            {/index/i.test(err) && (
              <div style={{ marginTop: 6, fontSize: 13, opacity: 0.85 }}>
                Usually a missing composite index — run <code>firebase deploy --only firestore:indexes</code>.
              </div>
            )}
          </div>
        )}

        {/* ── Best calls: the shortlist to actually send a prospect ────────── */}
        {tab === 'best' && (
          <div>
            {!bestFeed.length && (
              <div style={S.tableCard}>
                <div style={S.empty}>
                  No scored leads yet. Calls are transcribed and ranked automatically
                  after each sync.
                </div>
              </div>
            )}

            {bestFeed.map((entry, i) => {
              if (entry.kind === 'text') {
                const t = entry.item;
                const tier = entry.score >= 70 ? S.scoreHot : entry.score >= 50 ? S.scoreWarm : S.scoreCool;
                return (
                  <div key={t.id} style={S.bestCard}>
                    <div style={S.bestHead}>
                      <div style={{ ...S.scoreBadge, ...tier }}>
                        <div style={S.scoreNum}>{bestSort === 'recent' ? entry.score : i + 1}</div>
                      </div>

                      <div style={S.bestBody}>
                        <div style={S.bestJob}>{t.textHeadline}</div>
                        <div style={S.bestMetaRow}>
                          <span style={S.metaTag}>{t.textTrade}</span>
                          {t.textUrgent && <span style={S.urgentTag}>urgent</span>}
                          <span style={S.channelTag}>text</span>
                          <span style={S.metaDim}>{fmtPhone(t.fromNumber)}</span>
                        </div>
                        <div style={S.bestSummary}>{t.textSummary}</div>
                        <div style={S.bestQuote}>“{t.textQuote}”</div>
                      </div>

                      <div style={S.bestActions}>
                        <button
                          style={S.linkBtn}
                          onClick={() => setExpandedId(expandedId === t.id ? null : t.id)}
                        >
                          {expandedId === t.id ? 'Hide message' : 'Full message'}
                        </button>
                      </div>
                    </div>

                    {expandedId === t.id && (
                      <div style={S.transcriptBox}>{t.body}</div>
                    )}
                  </div>
                );
              }
              const c = entry.item;
              const a = c.callAssessment || {};
              const isPlaying = playingId === c.id;
              const open = expandedId === c.id;
              // Badge = the AI's own rating of the call. Position in the list = lead
              // quality (job size, urgency, replacement intent). Two different questions.
              const tier = c.callScore >= 70 ? S.scoreHot : c.callScore >= 50 ? S.scoreWarm : S.scoreCool;
              return (
                <div key={c.id} style={{ ...S.bestCard, ...(isPlaying ? S.bestCardPlaying : null) }}>
                  <div style={S.bestHead}>
                    <div style={{ ...S.scoreBadge, ...tier }}>
                      <div style={S.scoreNum}>{bestSort === 'recent' ? entry.score : i + 1}</div>
                    </div>

                    <div style={S.bestBody}>
                      <div style={S.bestJob}>{headlineFor(a.jobDescription)}</div>
                      <div style={S.bestMetaRow}>
                        <span style={S.metaTag}>{a.serviceType}</span>
                        {a.urgency === 'emergency' && <span style={S.urgentTag}>emergency</span>}
                        {a.bookedOrScheduled && <span style={S.bookedTag}>booked</span>}
                        <span style={S.metaDim}>{fmtPhone(c.fromNumber)}</span>
                        <span style={S.metaDim}>{fmtDuration(c.durationSeconds)}</span>
                      </div>
                      <div style={S.bestSummary}>{a.summary}</div>
                      {a.bestQuote && <div style={S.bestQuote}>“{a.bestQuote}”</div>}
                    </div>

                    <div style={S.bestActions}>
                      {c.recordingStatus === 'ready' && (
                        <button
                          style={{ ...S.playBtn, ...(isPlaying ? S.playBtnActive : null) }}
                          onClick={() => play(c)}
                          disabled={loadingId === c.id}
                        >
                          {loadingId === c.id ? '···' : isPlaying ? '❚❚' : '▶'}
                          <span style={S.playLbl}>{isPlaying ? 'Playing' : 'Play'}</span>
                        </button>
                      )}
                      {c.recordingStatus === 'ready' && (
                        <button
                          style={S.dlBtn}
                          onClick={() => download(c, 'clip')}
                          disabled={downloadingId === `${c.id}:clip`}
                          title={c.clipDurationSeconds
                            ? `Download the ${Math.round(c.clipDurationSeconds)}s highlight`
                            : 'Download the highlight'}
                        >
                          {downloadingId === `${c.id}:clip` ? 'Preparing…' : '↓ Clip'}
                          {c.clipDurationSeconds > 0 && (
                            <span style={S.dlHint}>{Math.round(c.clipDurationSeconds)}s</span>
                          )}
                        </button>
                      )}
                      {c.recordingStatus === 'ready' && (
                        <button
                          style={S.linkBtn}
                          onClick={() => download(c, 'full')}
                          disabled={downloadingId === `${c.id}:full`}
                        >
                          {downloadingId === `${c.id}:full` ? 'Preparing…' : 'Full call'}
                        </button>
                      )}
                      {c.transcript && (
                        <button
                          style={S.linkBtn}
                          onClick={() => setExpandedId(open ? null : c.id)}
                        >
                          {open ? 'Hide transcript' : 'Transcript'}
                        </button>
                      )}
                    </div>
                  </div>

                  {open && c.transcript && (
                    <div style={S.transcriptBox}>{c.transcript}</div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {tab === 'calls' && (
        <div style={S.tableCard}>
          <table style={S.table}>
            <thead>
              <tr>
                {['Direction', 'From', 'To', 'Caller', 'Duration', 'Status', ''].map((h, i) => (
                  <th key={i} style={S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} style={S.empty}>Loading calls…</td></tr>}
              {!loading && !filtered.length && (
                <tr><td colSpan={7} style={S.empty}>
                  No calls yet. They appear here as soon as the webhook fires, or after a sync.
                </td></tr>
              )}
              {filtered.map((c) => {
                const st = statusStyle(c.status);
                const isPlaying = playingId === c.id;
                const isHover = hoverId === c.id;
                const canPlay = c.recordingStatus === 'ready';
                return (
                  <tr
                    key={c.id}
                    style={{
                      ...S.tr,
                      background: isPlaying ? 'rgba(13,110,105,.05)' : isHover ? '#fafbfb' : 'transparent',
                    }}
                    onMouseEnter={() => setHoverId(c.id)}
                    onMouseLeave={() => setHoverId(null)}
                  >
                    <td style={S.td}>
                      <span style={{
                        ...S.dirPill,
                        background: c.direction === 'inbound' ? 'rgba(37,99,235,.1)' : 'rgba(147,51,234,.1)',
                        color: c.direction === 'inbound' ? '#1d4ed8' : '#7e22ce',
                      }}>
                        {c.direction === 'inbound' ? '↓' : '↑'} {c.direction || '—'}
                      </span>
                    </td>
                    <td style={S.tdNum}>{fmtPhone(c.fromNumber)}</td>
                    <td style={S.tdNum}>{fmtPhone(c.toNumber)}</td>
                    <td style={S.tdMuted}>{c.callerName || '—'}</td>
                    <td style={S.tdDur}>{fmtDuration(c.durationSeconds)}</td>
                    <td style={S.td}>
                      <span style={{ ...S.statusPill, background: st.bg, color: st.fg }}>
                        <span style={{ ...S.statusDot, background: st.dot }} />
                        {st.label}
                      </span>
                    </td>
                    <td style={{ ...S.td, textAlign: 'right' }}>
                      {canPlay ? (
                        <button
                          style={{ ...S.playBtn, ...(isPlaying ? S.playBtnActive : null) }}
                          onClick={() => play(c)}
                          disabled={loadingId === c.id}
                        >
                          {loadingId === c.id ? '···' : isPlaying ? '❚❚' : '▶'}
                          <span style={S.playLbl}>{isPlaying ? 'Playing' : 'Play'}</span>
                        </button>
                      ) : (
                        <span style={S.noRec}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        )}

        {tab === 'texts' && (
          <div style={S.tableCard}>
            <table style={S.table}>
              <thead>
                <tr>
                  {['From', 'Message', 'Status'].map((h, i) => (
                    <th key={i} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {!filteredTexts.length && (
                  <tr><td colSpan={3} style={S.empty}>
                    No texts yet. Inbound messages to this number appear here after a sync.
                  </td></tr>
                )}
                {filteredTexts.map((t) => {
                  const isHover = hoverId === t.id;
                  return (
                    <tr
                      key={t.id}
                      style={{ ...S.tr, background: isHover ? '#fafbfb' : 'transparent' }}
                      onMouseEnter={() => setHoverId(t.id)}
                      onMouseLeave={() => setHoverId(null)}
                    >
                      <td style={S.tdNum}>{fmtPhone(t.fromNumber)}</td>
                      <td style={S.msgCell}>{t.body || '—'}</td>
                      <td style={S.td}>
                        <span style={{ ...S.statusPill, background: 'rgba(34,197,94,.12)', color: '#15803d' }}>
                          <span style={{ ...S.statusDot, background: '#22c55e' }} />
                          {t.status || 'delivered'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div style={S.footNote}>
          Recordings stream from LeadFeed storage · links expire after 15 minutes
        </div>
      </div>
      )}

      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio ref={audioRef} onEnded={() => setPlayingId(null)} style={{ display: 'none' }} />
    </div>
  );
}

function Seg({ value, onChange, options }) {
  return (
    <div style={S.seg}>
      {options.map(([v, label]) => (
        <button
          key={v}
          onClick={() => onChange(v)}
          style={{
            ...S.segBtn,
            background: value === v ? '#0f3d3a' : 'transparent',
            color: value === v ? '#fff' : '#5b6b67',
          }}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

const FONT = '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif';

const S = {
  page: { minHeight: '100vh', background: '#f2f5f4', fontFamily: FONT, paddingBottom: 60 },

  // ── hero ──
  hero: {
    position: 'relative',
    background: 'linear-gradient(135deg,#0b2d2b 0%,#12403c 45%,#0a2523 100%)',
    overflow: 'hidden',
    borderBottom: '1px solid rgba(255,255,255,.06)',
  },
  heroGlowA: {
    position: 'absolute', top: -180, right: -120, width: 520, height: 520, borderRadius: '50%',
    background: 'radial-gradient(circle,rgba(45,212,191,.20),transparent 65%)', pointerEvents: 'none',
  },
  heroGlowB: {
    position: 'absolute', bottom: -240, left: -140, width: 560, height: 560, borderRadius: '50%',
    background: 'radial-gradient(circle,rgba(16,185,129,.14),transparent 65%)', pointerEvents: 'none',
  },
  heroInner: { position: 'relative', maxWidth: 1280, margin: '0 auto', padding: '26px 28px 44px' },
  heroTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' },
  logoChip: {
    background: '#fff', borderRadius: 14, padding: '14px 24px',
    boxShadow: '0 10px 30px -12px rgba(0,0,0,.55)', display: 'inline-flex',
  },
  leadfeedLogo: { height: 46, width: 'auto', display: 'block' },
  heroRight: { display: 'flex', alignItems: 'center', gap: 12 },

  heroBody: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
    gap: 24, marginTop: 34, flexWrap: 'wrap',
  },
  h1: {
    margin: 0, color: '#fff', fontSize: 'clamp(34px,5vw,52px)', fontWeight: 700,
    letterSpacing: '-.03em', lineHeight: 1,
  },
  heroMeta: { display: 'flex', alignItems: 'center', gap: 12, marginTop: 16, flexWrap: 'wrap' },
  numberPill: {
    background: 'rgba(255,255,255,.10)', border: '1px solid rgba(255,255,255,.18)',
    color: '#d7f5f0', borderRadius: 999, padding: '7px 16px',
    fontSize: 15, fontWeight: 600, fontVariantNumeric: 'tabular-nums',
  },
  heroMetaDim: { color: 'rgba(215,245,240,.55)', fontSize: 14 },

  clientCard: {
    background: '#fff', borderRadius: 18, padding: '18px 30px',
    boxShadow: '0 18px 44px -18px rgba(0,0,0,.6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  clientLogo: { height: 82, width: 'auto', display: 'block' },

  // ── auth ──
  authOk: {
    display: 'flex', alignItems: 'center', gap: 10,
    background: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.14)',
    borderRadius: 999, padding: '7px 8px 7px 14px', color: '#cfeae6', fontSize: 13,
  },
  authDot: { width: 7, height: 7, borderRadius: '50%', background: '#34d399', display: 'inline-block' },
  signOutBtn: {
    background: 'rgba(255,255,255,.12)', color: '#eafaf7', border: 'none',
    borderRadius: 999, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
  },
  signInBtn: {
    background: '#12c2a0', color: '#04231f', border: 'none', borderRadius: 999,
    padding: '10px 20px', fontSize: 14, fontWeight: 700, cursor: 'pointer',
    boxShadow: '0 8px 22px -10px rgba(18,194,160,.9)',
  },

  // ── body ──
  wrap: { maxWidth: 1280, margin: '0 auto', padding: '0 28px' },
  toolbar: {
    display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
    background: '#fff', border: '1px solid #e3eae8', borderRadius: 16,
    padding: 14, marginTop: -26, position: 'relative',
    boxShadow: '0 14px 40px -22px rgba(11,45,43,.5)',
  },
  countBox: {
    display: 'flex', alignItems: 'baseline', gap: 9,
    background: 'linear-gradient(135deg,#0f3d3a,#155e56)', color: '#fff',
    borderRadius: 12, padding: '11px 20px',
  },
  countNum: { fontSize: 26, fontWeight: 700, letterSpacing: '-.02em', lineHeight: 1 },
  countSub: { fontSize: 11, opacity: 0.75, marginTop: 2, whiteSpace: 'nowrap' },
  countLbl: { fontSize: 11.5, textTransform: 'uppercase', letterSpacing: '.11em', opacity: 0.75, fontWeight: 600 },

  seg: { display: 'inline-flex', background: '#f2f5f4', borderRadius: 11, padding: 4, gap: 3 },
  segBtn: {
    border: 'none', borderRadius: 8, padding: '8px 17px',
    fontSize: 13.5, fontWeight: 600, cursor: 'pointer', transition: 'all .15s',
  },
  searchWrap: { position: 'relative', flex: '1 1 260px', minWidth: 200 },
  searchIcon: { position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#9bada9', fontSize: 17 },
  search: {
    width: '100%', padding: '11px 14px 11px 36px', border: '1px solid #e3eae8',
    borderRadius: 11, fontSize: 14, background: '#fbfcfc', outline: 'none', fontFamily: FONT,
  },

  error: {
    background: '#fff1f1', color: '#a8231f', border: '1px solid #fbcaca',
    borderRadius: 12, padding: '13px 16px', marginTop: 14, fontSize: 14,
    display: 'flex', alignItems: 'center', flexWrap: 'wrap',
  },

  tableCard: {
    marginTop: 16, background: '#fff', border: '1px solid #e3eae8',
    borderRadius: 16, overflow: 'hidden', overflowX: 'auto',
    boxShadow: '0 10px 34px -24px rgba(11,45,43,.45)',
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 14.5, minWidth: 900 },
  th: {
    textAlign: 'left', padding: '15px 18px', fontSize: 11, letterSpacing: '.1em',
    textTransform: 'uppercase', color: '#93a5a1', fontWeight: 700,
    borderBottom: '1px solid #eef3f1', whiteSpace: 'nowrap', background: '#fcfdfd',
  },
  tr: { borderBottom: '1px solid #f2f6f5', transition: 'background .12s' },
  td: { padding: '14px 18px', verticalAlign: 'middle' },
  tdNum: { padding: '14px 18px', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', color: '#26332f', fontWeight: 500 },
  tdMuted: { padding: '14px 18px', color: '#849691' },
  tdDur: { padding: '14px 18px', fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: '#0f3d3a' },

  whenDay: { fontWeight: 600, color: '#1d2b28', fontSize: 14 },
  whenTime: { fontSize: 12.5, color: '#93a5a1', marginTop: 2 },

  dirPill: {
    display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px',
    borderRadius: 999, fontSize: 12.5, fontWeight: 600, textTransform: 'capitalize',
  },
  statusPill: {
    display: 'inline-flex', alignItems: 'center', gap: 7, padding: '5px 13px',
    borderRadius: 999, fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap',
  },
  statusDot: { width: 6, height: 6, borderRadius: '50%', display: 'inline-block' },

  playBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    background: '#0f3d3a', color: '#fff', border: 'none', borderRadius: 999,
    padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
    boxShadow: '0 6px 16px -8px rgba(15,61,58,.8)', transition: 'all .15s',
  },
  playBtnActive: { background: '#12c2a0', color: '#04231f' },
  playLbl: { fontSize: 13, fontWeight: 600 },
  noRec: { color: '#c3cfcb', fontSize: 15 },

  empty: { padding: '54px 18px', textAlign: 'center', color: '#93a5a1', fontSize: 14.5 },
  msgCell: { padding: '14px 18px', color: '#26332f', lineHeight: 1.5, maxWidth: 620 },
  gate: {
    maxWidth: 460, margin: '0 auto', padding: '44px 32px 40px', textAlign: 'center',
    background: '#fff', border: '1px solid #e6eaea', borderRadius: 20,
    boxShadow: '0 18px 40px -24px rgba(9,30,28,.28)',
  },
  gateIcon:  { fontSize: 34, lineHeight: 1, marginBottom: 14 },
  gateTitle: { fontSize: 19, fontWeight: 700, color: '#0b2d2b', letterSpacing: '-.01em' },
  gateSub:   { marginTop: 10, fontSize: 13.5, lineHeight: 1.6, color: '#6b7c7a' },
  gateForm: { display: 'flex', flexDirection: 'column', gap: 10, width: 'min(320px, 100%)', margin: '18px auto 0' },
  gateInput: { font: 'inherit', padding: '11px 12px', border: '1px solid #e5e7eb', borderRadius: 8, outline: 'none' },
  gateBtn: {
    marginTop: 22, padding: '11px 22px', border: 'none', borderRadius: 11,
    background: '#0b2d2b', color: '#fff', fontSize: 13.5, fontWeight: 600, cursor: 'pointer',
  },
  bestIntro: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 20,
    flexWrap: 'wrap', padding: '16px 20px', marginBottom: 14, borderRadius: 14,
    background: 'linear-gradient(135deg,rgba(13,110,105,.06),rgba(13,110,105,.02))',
    border: '1px solid rgba(13,110,105,.15)' },
  bestIntroTitle: { fontSize: 15, fontWeight: 800, color: '#0f172a', marginBottom: 4 },
  bestIntroSub: { fontSize: 13, color: '#64748b', lineHeight: 1.5, maxWidth: 620 },
  pipelinePill: { textAlign: 'right' },
  totalsRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(190px,1fr))', gap: 14, marginBottom: 18 },
  totalCard: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '18px 20px', boxShadow: '0 1px 2px rgba(16,24,40,.04)' },
  totalCardAccent: { borderColor: 'rgba(13,110,105,.45)', boxShadow: '0 0 0 3px rgba(13,110,105,.08)' },
  totalNum: { fontSize: 40, fontWeight: 800, letterSpacing: '-.03em', color: '#0f172a', lineHeight: 1 },
  totalLbl: { fontSize: 14, fontWeight: 700, color: '#0f172a', marginTop: 8 },
  totalSub: { fontSize: 12, color: '#64748b', marginTop: 2 },
  pipelineNum: { fontSize: 26, fontWeight: 800, color: '#0d6e69', letterSpacing: '-.02em' },
  pipelineLbl: { fontSize: 11, color: '#94a3b8', fontWeight: 600 },

  bestCard: { background: '#fff', border: '1px solid #e8ecec', borderRadius: 14, padding: 16,
    marginBottom: 10, transition: 'box-shadow .15s, border-color .15s' },
  bestCardPlaying: { borderColor: 'rgba(13,110,105,.45)', boxShadow: '0 0 0 3px rgba(13,110,105,.08)' },
  bestHead: { display: 'flex', gap: 16, alignItems: 'flex-start' },

  scoreBadge: { flexShrink: 0, width: 58, borderRadius: 12, padding: '10px 0', textAlign: 'center' },
  scoreHot: { background: 'rgba(5,150,105,.12)', color: '#047857' },
  scoreWarm: { background: 'rgba(202,138,4,.12)', color: '#a16207' },
  scoreCool: { background: 'rgba(100,116,139,.1)', color: '#64748b' },
  scoreNum: { fontSize: 22, fontWeight: 800, lineHeight: 1 },
  scoreRank: { fontSize: 10, fontWeight: 700, opacity: .7, marginTop: 3 },

  bestBody: { flex: 1, minWidth: 0 },
  bestJob: { fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 6 },
  bestMetaRow: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 },
  ticketPill: { fontSize: 12, fontWeight: 800, color: '#047857', background: 'rgba(5,150,105,.1)',
    padding: '3px 9px', borderRadius: 999 },
  channelTag: { fontSize: 11, fontWeight: 700, color: '#475569', background: '#f1f5f9',
    border: '1px solid #e2e8f0', borderRadius: 999, padding: '2px 9px', letterSpacing: '.02em' },
  metaTag: { fontSize: 11, fontWeight: 700, color: '#475569', background: '#f1f5f9',
    padding: '3px 9px', borderRadius: 999 },
  urgentTag: { fontSize: 11, fontWeight: 800, color: '#b91c1c', background: 'rgba(220,38,38,.1)',
    padding: '3px 9px', borderRadius: 999 },
  bookedTag: { fontSize: 11, fontWeight: 800, color: '#1d4ed8', background: 'rgba(37,99,235,.1)',
    padding: '3px 9px', borderRadius: 999 },
  metaDim: { fontSize: 12, color: '#94a3b8' },
  bestSummary: { fontSize: 13.5, color: '#475569', lineHeight: 1.55 },
  bestQuote: { marginTop: 8, paddingLeft: 12, borderLeft: '3px solid rgba(13,110,105,.35)',
    fontSize: 13.5, fontStyle: 'italic', color: '#334155', lineHeight: 1.5 },
  redFlag: { marginTop: 8, fontSize: 12.5, color: '#92400e', background: 'rgba(245,158,11,.09)',
    border: '1px solid rgba(245,158,11,.2)', borderRadius: 8, padding: '7px 10px', lineHeight: 1.45 },
  bestActions: { display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end', flexShrink: 0 },
  linkBtn: { background: 'none', border: 'none', color: '#0d6e69', fontSize: 12, fontWeight: 700,
    cursor: 'pointer', padding: '4px 2px', whiteSpace: 'nowrap' },
  transcriptBox: { marginTop: 14, padding: 14, background: '#f8fafc', border: '1px solid #e8ecec',
    borderRadius: 10, fontSize: 13, lineHeight: 1.65, color: '#334155', whiteSpace: 'pre-wrap',
    maxHeight: 340, overflowY: 'auto' },

  dlBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, background: '#0d6e69',
    color: '#fff', border: 'none', borderRadius: 9, padding: '8px 13px', fontSize: 12.5,
    fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' },
  dlHint: { fontSize: 11, fontWeight: 600, opacity: .75 },

  footNote: { marginTop: 16, textAlign: 'center', fontSize: 12.5, color: '#93a5a1' },
};
